import React, { useEffect, useMemo, useState } from 'react';
import { obtenerRecursosAyuda, MODULOS_AYUDA, nombreModulo, youtubeId, miniatura } from '../services/ayudaService';
import { copiarTexto } from '../utils/compartir';
import { useToast } from '../context/ToastContext';
import VideoAyudaModal from './VideoAyudaModal';
import CalculadoraPrecio from './CalculadoraPrecio';
import CampoNumero from './CampoNumero';
import InstalarApp from './InstalarApp';

// Herramientas y Ayuda: videos paso a paso de cada módulo, guías descargables,
// plantillas de mensajes para copiar, calculadora de precio y enlaces útiles.
// El contenido lo carga la admin desde su panel (sql/138). Es el soporte de Kontrol:
// todo se resuelve con videos y guías, sin depender de una persona.

const P = 'var(--color-primario,#DB2777)';
const SECCIONES = [
  { clave: 'videos', txt: '▶️ Videos' },
  { clave: 'guias', txt: '📘 Guías' },
  { clave: 'plantillas', txt: '💬 Plantillas' },
  { clave: 'calculadora', txt: '🧮 Calculadora' },
  { clave: 'enlaces', txt: '🔗 Enlaces' },
];
const tarjeta = { background: '#fff', border: '1px solid #EEE7FB', borderRadius: '14px', padding: '14px 16px' };
const vacio = (txt) => <p style={{ ...tarjeta, color: '#6B7280', margin: 0 }}>{txt}</p>;

function Videos({ videos }) {
  const [buscar, setBuscar] = useState('');
  const [abierto, setAbierto] = useState(null); // { lista, i }
  const q = buscar.trim().toLowerCase();
  const filtrados = videos.filter((v) => !q || `${v.titulo} ${v.descripcion || ''} ${nombreModulo(v.modulo)}`.toLowerCase().includes(q));
  const grupos = MODULOS_AYUDA
    .map((m) => ({ ...m, lista: filtrados.filter((v) => v.modulo === m.clave) }))
    .filter((g) => g.lista.length);

  if (!videos.length) return vacio('Muy pronto: aquí verás videos paso a paso de cada parte de Kontrol para que aprendas a tu ritmo.');
  return (
    <>
      <input className="k-input" value={buscar} onChange={(e) => setBuscar(e.target.value)} placeholder="Buscar un video (ej. cotización, inventario)"
        aria-label="Buscar un video" style={{ width: '100%', marginBottom: '14px', boxSizing: 'border-box' }} />
      {!grupos.length && vacio('No encontramos videos con esa palabra.')}
      {grupos.map((g) => (
        <section key={g.clave} style={{ marginBottom: '18px' }}>
          <h3 style={{ margin: '0 0 8px', fontSize: '15px' }}>{g.nombre}</h3>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))', gap: '10px' }}>
            {g.lista.map((v, i) => {
              const id = youtubeId(v.url);
              return (
                <button key={v.id} type="button" onClick={() => setAbierto({ lista: g.lista, i })}
                  style={{ ...tarjeta, padding: 0, overflow: 'hidden', textAlign: 'left', cursor: 'pointer' }}>
                  {id
                    ? <img src={miniatura(id)} alt="" loading="lazy" style={{ width: '100%', aspectRatio: '16 / 9', objectFit: 'cover', display: 'block' }} />
                    : <div style={{ aspectRatio: '16 / 9', display: 'grid', placeItems: 'center', background: '#F5F3FF', fontSize: '28px' }}>▶️</div>}
                  <div style={{ padding: '10px 12px' }}>
                    <b style={{ display: 'block', fontSize: '14px' }}>{v.titulo}</b>
                    {v.descripcion && <span style={{ fontSize: '12.5px', color: '#6B7280' }}>{v.descripcion}</span>}
                  </div>
                </button>
              );
            })}
          </div>
        </section>
      ))}
      {abierto && <VideoAyudaModal videos={abierto.lista} inicial={abierto.i} onCerrar={() => setAbierto(null)} />}
    </>
  );
}

function Guias({ guias }) {
  if (!guias.length) return vacio('Muy pronto: aquí encontrarás guías para vender más en cada temporada.');
  return (
    <div style={{ display: 'grid', gap: '10px' }}>
      {guias.map((g) => (
        <div key={g.id} style={{ ...tarjeta, display: 'flex', gap: '12px', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap' }}>
          <div style={{ minWidth: 0, flex: '1 1 260px' }}>
            <b style={{ display: 'block' }}>📘 {g.titulo}</b>
            {g.descripcion && <span style={{ fontSize: '13px', color: '#6B7280' }}>{g.descripcion}</span>}
          </div>
          {g.url && <a href={g.url} target="_blank" rel="noopener noreferrer" className="k-btn-soft" style={{ textDecoration: 'none', fontWeight: 700 }}>Descargar</a>}
        </div>
      ))}
    </div>
  );
}

function Plantillas({ plantillas }) {
  const { mostrarToast } = useToast();
  const copiar = async (p) => {
    const ok = await copiarTexto(p.contenido || '');
    mostrarToast(ok === false ? 'No se pudo copiar. Mantén presionado el texto para copiarlo.' : 'Mensaje copiado. Cambia lo que está entre [ ] y envíalo.', ok === false ? 'error' : 'success');
  };
  if (!plantillas.length) return vacio('Muy pronto: aquí tendrás mensajes listos para copiar y mandar a tus clientes.');
  return (
    <>
      <p style={{ margin: '0 0 12px', fontSize: '13.5px', color: '#6B7280' }}>Copia el mensaje, cambia lo que está entre [corchetes] y envíalo por WhatsApp o Instagram.</p>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', gap: '10px' }}>
        {plantillas.map((p) => (
          <div key={p.id} style={{ ...tarjeta, display: 'grid', gap: '8px', alignContent: 'start' }}>
            <b>{p.titulo}</b>
            {p.descripcion && <span style={{ fontSize: '12.5px', color: '#6B7280' }}>{p.descripcion}</span>}
            <p style={{ margin: 0, padding: '10px 12px', background: '#F9FAFB', borderRadius: '10px', fontSize: '14px', whiteSpace: 'pre-wrap', lineHeight: 1.45 }}>{p.contenido}</p>
            <button type="button" onClick={() => copiar(p)}
              style={{ justifySelf: 'start', padding: '8px 14px', borderRadius: '8px', border: 'none', background: P, color: '#fff', fontWeight: 700, cursor: 'pointer' }}>
              Copiar mensaje
            </button>
          </div>
        ))}
      </div>
    </>
  );
}

function Calculadora() {
  const [costo, setCosto] = useState('');
  const [precio, setPrecio] = useState('');
  return (
    <div style={{ ...tarjeta, maxWidth: '560px' }}>
      <p style={{ margin: '0 0 12px', fontSize: '14px', color: '#4B5563' }}>Escribe cuánto te cuesta un producto y te digo a cuánto venderlo para ganar lo que quieres.</p>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px', marginBottom: '12px' }}>
        <CampoNumero etiqueta="Precio de costo" prefijo="L" valor={costo} onChange={setCosto} placeholder="0.00" />
        <CampoNumero etiqueta="Precio de venta (opcional)" prefijo="L" valor={precio} onChange={setPrecio} placeholder="0.00" />
      </div>
      <CalculadoraPrecio costo={costo} precioVenta={precio} onAplicarPrecio={setPrecio} />
    </div>
  );
}

function Enlaces({ enlaces }) {
  return (
    <div style={{ display: 'grid', gap: '10px' }}>
      <div style={{ ...tarjeta, display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '12px', flexWrap: 'wrap' }}>
        <div><b style={{ display: 'block' }}>📲 Instala Kontrol en tu celular</b><span style={{ fontSize: '13px', color: '#6B7280' }}>En Android: menú del navegador (⋮) → Instalar app. En iPhone: Compartir → Agregar a inicio.</span></div>
        <InstalarApp className="k-btn-soft" />
      </div>
      {enlaces.map((e) => (
        <a key={e.id} href={e.url} target={e.url?.startsWith('mailto:') ? undefined : '_blank'} rel="noopener noreferrer"
          style={{ ...tarjeta, display: 'block', textDecoration: 'none', color: 'inherit' }}>
          <b style={{ display: 'block' }}>🔗 {e.titulo}</b>
          {e.descripcion && <span style={{ fontSize: '13px', color: '#6B7280' }}>{e.descripcion}</span>}
        </a>
      ))}
    </div>
  );
}

export default function AyudaView({ onVolver }) {
  const [recursos, setRecursos] = useState(null);
  const [error, setError] = useState(false);
  const [seccion, setSeccion] = useState('videos');

  useEffect(() => {
    obtenerRecursosAyuda({ forzar: true }).then(setRecursos).catch(() => setError(true));
  }, []);

  const por = useMemo(() => {
    const activos = (recursos || []).filter((r) => r.activo);
    return {
      videos: activos.filter((r) => r.tipo === 'video'),
      guias: activos.filter((r) => r.tipo === 'guia'),
      plantillas: activos.filter((r) => r.tipo === 'plantilla'),
      enlaces: activos.filter((r) => r.tipo === 'enlace'),
    };
  }, [recursos]);

  return (
    <div className="k-page">
      <div className="k-page-head">
        <h2 className="k-page-title">🧰 Herramientas y Ayuda</h2>
        <button onClick={onVolver} className="k-btn-soft">← Volver</button>
      </div>
      <p style={{ margin: '0 0 14px', color: '#6B7280', fontSize: '14px' }}>Aprende a usar cada parte de Kontrol con videos cortos, y usa estas herramientas para vender más.</p>

      <div role="tablist" aria-label="Secciones de ayuda" style={{ display: 'flex', gap: '6px', flexWrap: 'wrap', marginBottom: '16px' }}>
        {SECCIONES.map((s) => (
          <button key={s.clave} role="tab" aria-selected={seccion === s.clave} onClick={() => setSeccion(s.clave)}
            style={{ padding: '8px 14px', borderRadius: '999px', fontSize: '14px', fontWeight: 700, cursor: 'pointer',
              border: `1px solid ${seccion === s.clave ? P : '#E5E7EB'}`, background: seccion === s.clave ? P : '#fff', color: seccion === s.clave ? '#fff' : '#4B5563' }}>
            {s.txt}
          </button>
        ))}
      </div>

      {seccion === 'calculadora' ? <Calculadora />
        : error ? vacio('No pudimos cargar la ayuda. Revisa tu conexión e intenta de nuevo.')
        : !recursos ? <p style={{ color: '#6B7280' }}>Cargando…</p>
        : seccion === 'videos' ? <Videos videos={por.videos} />
        : seccion === 'guias' ? <Guias guias={por.guias} />
        : seccion === 'plantillas' ? <Plantillas plantillas={por.plantillas} />
        : <Enlaces enlaces={por.enlaces} />}
    </div>
  );
}
